import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie, X, Settings, Check } from 'lucide-react';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    functional: true,
    analytics: true
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem('decoded_cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consent = {
      essential: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('decoded_cookie_consent', JSON.stringify(consent));
    setVisible(false);
  };

  const acceptSelected = () => {
    const consent = {
      ...preferences,
      essential: true, // Always required
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('decoded_cookie_consent', JSON.stringify(consent));
    setVisible(false);
  };

  const rejectOptional = () => {
    const consent = {
      essential: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('decoded_cookie_consent', JSON.stringify(consent));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6 pointer-events-none">
      <div className="max-w-4xl mx-auto pointer-events-auto">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {!showSettings ? (
            // Simple consent view
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">We use cookies</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                    By clicking "Accept All", you consent to our use of cookies. 
                    <Link to="/privacy" className="text-blue-600 hover:underline ml-1">
                      Learn more
                    </Link>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={acceptAll}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept All
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={rejectOptional}
                      data-testid="cookie-reject"
                    >
                      Essential Only
                    </Button>
                    <Button 
                      variant="ghost" 
                      onClick={() => setShowSettings(true)}
                      data-testid="cookie-settings"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Customize
                    </Button>
                  </div>
                </div>
                <button 
                  onClick={rejectOptional}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 flex-shrink-0"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            // Detailed settings view
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Cookie Preferences</h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Essential */}
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <input 
                    type="checkbox" 
                    checked={true} 
                    disabled 
                    className="mt-1 rounded"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">Essential Cookies</span>
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Required</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">
                      Required for basic site functionality, authentication, and security. Cannot be disabled.
                    </p>
                  </div>
                </div>

                {/* Functional */}
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <input 
                    type="checkbox" 
                    checked={preferences.functional}
                    onChange={(e) => setPreferences({...preferences, functional: e.target.checked})}
                    className="mt-1 rounded"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-slate-900">Functional Cookies</span>
                    <p className="text-sm text-slate-500 mt-1">
                      Remember your preferences and settings (language, theme, sidebar state).
                    </p>
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <input 
                    type="checkbox" 
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                    className="mt-1 rounded"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-slate-900">Analytics Cookies</span>
                    <p className="text-sm text-slate-500 mt-1">
                      Help us understand how you use Decoded so we can improve the experience.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={acceptSelected}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Save Preferences
                </Button>
                <Button 
                  variant="outline" 
                  onClick={acceptAll}
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
